/* Native-scroll material explorer; no wheel interception or external animation library. */
(() => {
  'use strict';
  if (customElements.get('knox-finish-story')) return;
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const mix = (a, b, amount) => a + (b - a) * amount;
  const ease = (value) => value * value * (3 - 2 * value);
  const blend = (a, b, amount) => ({
    x: mix(a.x, b.x, amount),
    y: mix(a.y, b.y, amount),
    scale: mix(a.scale, b.scale, amount)
  });

  class KnoxFinishStory extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('[data-finish-viewport]');
      this.camera = this.querySelector('[data-finish-camera]');
      this.target = this.querySelector('[data-finish-target]');
      this.leader = this.querySelector('[data-finish-leader]');
      this.overview = this.querySelector('[data-finish-overview-panel]');
      this.overviewChoice = this.querySelector('[data-finish-overview]');
      this.steps = [...this.querySelectorAll('[data-finish-step]')];
      this.choices = [...this.querySelectorAll('[data-finish-choice]')];
      this.pin = this.querySelector('.knox-finish-story__pin');
      this.layout = this.querySelector('.knox-finish-story__layout');
      if (!this.viewport || !this.camera || !this.pin || this.steps.length < 2) return;

      this.secondaryTarget = this.target.cloneNode(false);
      this.secondaryTarget.removeAttribute('data-finish-target');
      this.secondaryTarget.setAttribute('data-finish-secondary', '');
      this.viewport.appendChild(this.secondaryTarget);
      if (this.leader) {
        this.secondaryLeader = this.leader.cloneNode(false);
        this.secondaryLeader.removeAttribute('data-finish-leader');
        this.viewport.appendChild(this.secondaryLeader);
      }

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.ratio = Number(this.dataset.imageRatio) || 8 / 3;
      this.style.setProperty('--finish-image-ratio', String(this.ratio));
      this.points = this.steps.map(step => ({
        x: clamp(Number(step.dataset.focusX)),
        y: clamp(Number(step.dataset.focusY)),
        scale: clamp(Number(step.dataset.focusScale), 1, 1.6)
      }));
      this.active = -1;
      this.lastIndex = this.steps.length - 1;
      this.totalLabel = String(this.steps.length).padStart(2, '0');
      this.overviewPoint = { x: .5, y: .5, scale: 1 };
      this.currentPoint = { ...this.overviewPoint };
      this.inView = true;
      this.compactMedia = matchMedia('(max-width: 999px), (max-height: 600px)');
      this.motionMedia = matchMedia('(prefers-reduced-motion: reduce)');
      this.classList.add('is-interactive');
      this.querySelector('.knox-finish-story__selector').hidden = false;
      if (this.overviewChoice) {
        this.overviewChoice.hidden = false;
        this.overviewChoice.addEventListener('click', () => this.select(-1), options);
      }

      this.choices.forEach((choice, index) => {
        choice.addEventListener('click', () => this.select(index), options);
        choice.addEventListener('keydown', event => {
          let next = index;
          if (event.key === 'ArrowRight') next = Math.min(this.lastIndex, index + 1);
          else if (event.key === 'ArrowLeft') next = Math.max(0, index - 1);
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.lastIndex;
          else return;
          event.preventDefault();
          this.choices[next].focus({ preventScroll: true });
          this.select(next);
        }, options);
      });

      this.viewport.addEventListener('pointerdown', event => {
        if (event.target.closest?.('button, a')) return;
        if (!this.compact || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        this.viewport.setPointerCapture(event.pointerId);
      }, { ...options, passive: true });
      this.viewport.addEventListener('pointerup', event => {
        const start = this.pointerStart;
        this.pointerStart = null;
        if (!this.compact || !start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          this.select(clamp(this.active + (dx < 0 ? 1 : -1), -1, this.lastIndex));
        }
      }, { ...options, passive: true });
      this.viewport.addEventListener('pointercancel', () => { this.pointerStart = null; }, options);
      this.viewport.addEventListener('dragstart', event => event.preventDefault(), options);

      window.addEventListener('scroll', () => {
        if (!this.compact && this.inView) this.schedule(false);
      }, { ...options, passive: true });
      window.addEventListener('resize', () => this.schedule(true), { ...options, passive: true });
      window.addEventListener('load', () => this.schedule(true), options);
      this.compactMedia.addEventListener('change', () => this.configure(), options);
      this.motionMedia.addEventListener('change', () => this.configure(), options);
      this.steps.forEach((step, index) => {
        step.addEventListener('focusin', () => {
          if (this.compact && index !== this.active) this.select(index);
        }, options);
      });

      this.intersectionObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.classList.toggle('is-in-view', this.inView);
        if (!this.inView) this.lastPaint = 0;
        if (this.inView) this.schedule(true);
      }, { rootMargin: '240px 0px' });
      this.intersectionObserver.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.schedule(true));
      this.resizeObserver.observe(this);
      document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(true); });
      this.configure();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.secondaryTarget?.remove();
      this.secondaryLeader?.remove();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.motionFrame);
      this.frame = 0;
      this.motionFrame = 0;
      this.needsMeasure = false;
    }

    configure() {
      this.compact = this.compactMedia.matches;
      this.reduced = this.motionMedia.matches;
      this.classList.toggle('is-compact', this.compact);
      this.classList.toggle('is-reduced', this.reduced);
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      this.lastPaint = 0;
      this.activeRendered = false;
      this.hasPainted = false;
      this.classList.remove('has-story-frame');
      this.scrollProgress = null;
      this.clearFocus();
      this.setActive(this.active);
      this.schedule(true);
    }

    schedule(measure) {
      this.needsMeasure = this.needsMeasure || measure;
      if (this.frame) return;
      this.frame = requestAnimationFrame(now => {
        this.frame = 0;
        if (!this.isConnected || !this.controller) return;
        if (this.needsMeasure) {
          this.needsMeasure = false;
          this.measure();
        }
        if (this.compact) {
          if (this.motionFrame) return;
          const point = this.active < 0 ? this.overviewPoint : this.points[this.active];
          this.paint(point, [{ index: this.active, opacity: this.active >= 0 ? 1 : 0 }]);
        } else {
          this.followScroll(now);
        }
      });
    }

    measure() {
      const header = [...document.querySelectorAll('header.shopify-section--header')]
        .find(element => element.getBoundingClientRect().height > 0);
      this.headerHeight = header?.getBoundingClientRect().height || 0;
      const preview = document.querySelector('#PBarNextFrame');
      const previewHeight = preview ? Math.min(preview.getBoundingClientRect().height, 64) : 0;
      const setLength = (property, value) => {
        const next = `${Math.round(value)}px`;
        if (this.style.getPropertyValue(property) !== next) this.style.setProperty(property, next);
      };
      const headingTop = this.headerHeight + 24;
      setLength('--finish-heading-top', headingTop);
      const top = headingTop;
      this.availableHeight = Math.max(260, innerHeight - previewHeight - top - 20);
      if (!this.compact) {
        const viewportRect = this.viewport.getBoundingClientRect();
        const controlsHeight = this.pin.getBoundingClientRect().height - viewportRect.height;
        const columnWidth = this.pin.getBoundingClientRect().width;
        const photoHeight = Math.min(columnWidth / this.ratio, Math.max(160, this.availableHeight - controlsHeight));
        setLength('--finish-photo-height', photoHeight);
        setLength('--finish-photo-width', photoHeight * this.ratio);
      }
      const contentHeight = Math.max(...this.steps.map(step => step.querySelector('.knox-finish-story__card').getBoundingClientRect().height));
      const pinHeight = Math.max(this.pin.getBoundingClientRect().height, contentHeight + 48);
      setLength('--finish-top', top);
      setLength('--finish-panel-height', pinHeight);
      this.stepDistance = clamp(this.availableHeight * .6, 460, 620);
      setLength('--finish-journey-height', pinHeight + this.stepDistance * (this.steps.length + .18));
      this.anchor = top + pinHeight / 2;
      const rootTop = this.getBoundingClientRect().top;
      this.overviewCentre = this.layout.getBoundingClientRect().top - rootTop + pinHeight / 2;
      this.centres = this.steps.map((step, index) => this.overviewCentre + this.stepDistance * (index + 1));
      const viewportRect = this.viewport.getBoundingClientRect();
      this.width = viewportRect.width;
      this.height = viewportRect.height;
      const baseWidth = Math.max(this.width, this.height * this.ratio);
      this.imageWidth = baseWidth;
      this.imageHeight = baseWidth / this.ratio;
      this.overviewPoint = { x: .5, y: .5, scale: 1 };
      if (!this.hasPainted) this.currentPoint = { ...this.overviewPoint };
      this.camera.style.width = `${this.imageWidth}px`;
      this.camera.style.height = `${this.imageHeight}px`;
    }

    followScroll(now = performance.now()) {
      if (!this.centres || !this.width) return;
      const position = this.anchor - this.getBoundingClientRect().top;
      const destination = clamp((position - this.overviewCentre) / this.stepDistance, 0, this.points.length);
      if (this.scrollProgress === null || !this.hasPainted) this.scrollProgress = destination;
      const elapsed = this.lastPaint ? clamp(now - this.lastPaint, 0, 64) : 1000 / 60;
      this.lastPaint = now;
      this.scrollProgress = this.reduced || !this.inView ? destination
        : mix(this.scrollProgress, destination, 1 - Math.exp(-elapsed / 90));
      if (Math.abs(this.scrollProgress - destination) < .0005) this.scrollProgress = destination;
      const frame = this.storyFrame(this.scrollProgress);
      this.setActive(frame.active);
      this.paint(frame.point, frame.markers);
      if (this.inView && !this.reduced && this.scrollProgress !== destination) this.schedule(false);
      else this.lastPaint = 0;
    }

    storyFrame(progress) {
      const segment = Math.min(this.lastIndex, Math.floor(progress));
      // The middle 44% around each product is a reading interval. Native scrolling
      // continues while both the camera and the product information remain still.
      const amount = ease(clamp((progress - segment - .22) / .56));
      const from = segment - 1;
      const to = segment;
      const fromPoint = from < 0 ? this.overviewPoint : this.points[from];
      const toPoint = this.points[to];
      const active = amount >= .5 ? to : from;
      const markers = this.transitionMarkers(from, to, amount);
      return { active, point: blend(fromPoint, toPoint, amount), markers };
    }

    transitionMarkers(from, to, amount) {
      // Coincident concealed products share a location: change its number without
      // stacking two circles and two numbers on top of one another.
      if (from >= 0 && to >= 0 && this.pointDistance(this.points[from], this.points[to]) < 1) {
        return [{ index: amount >= .5 ? to : from, opacity: 1 }];
      }
      return [
        { index: from, opacity: from < 0 ? 0 : 1 - ease(clamp((amount - .22) / .68)) },
        { index: to, opacity: to < 0 ? 0 : ease(clamp((amount - .08) / .72)) }
      ];
    }

    pointDistance(a, b) {
      return Math.max(
        Math.abs(a.x - b.x) * this.imageWidth * Math.max(a.scale, b.scale),
        Math.abs(a.y - b.y) * this.imageHeight * Math.max(a.scale, b.scale),
        Math.abs(a.scale - b.scale) * Math.max(this.width, this.height)
      );
    }

    clearFocus() {
      this.focusVisible = false;
      this.classList.remove('is-focused');
      if (this.target) this.target.style.opacity = '0';
      if (this.secondaryTarget) this.secondaryTarget.style.opacity = '0';
      if (this.leader) this.leader.style.opacity = '0';
      if (this.secondaryLeader) this.secondaryLeader.style.opacity = '0';
    }

    setActive(index, announce = false) {
      const changed = index !== this.active;
      if (!changed && this.activeRendered) return;
      this.activeRendered = true;
      this.active = index;
      const isOverview = index < 0;
      this.classList.toggle('is-overview', isOverview);
      this.steps.forEach((step, current) => {
        step.classList.toggle('is-active', current === index);
        step.hidden = this.compact && current !== index;
        step.inert = current !== index;
        step.setAttribute('aria-hidden', String(current !== index));
      });
      if (this.overview) {
        this.overview.hidden = this.compact && !isOverview;
        this.overview.inert = !isOverview;
        this.overview.setAttribute('aria-hidden', String(!isOverview));
      }
      this.overviewChoice?.setAttribute('aria-pressed', String(isOverview));
      this.choices.forEach((choice, current) => choice.setAttribute('aria-pressed', String(current === index)));
      if (isOverview) {
        this.querySelector('[data-finish-count]').textContent = 'Full view';
        if (announce && changed) this.querySelector('[data-finish-status]').textContent = 'Full kitchen view.';
        return;
      }
      const number = String(index + 1).padStart(2, '0');
      this.querySelector('[data-finish-number]').textContent = number;
      const combined = this.steps[index].dataset.combined === 'true';
      this.target.toggleAttribute('data-combined', combined);
      const pair = this.querySelector('[data-finish-pair]');
      if (pair) {
        pair.hidden = !combined;
        this.querySelector('[data-finish-number]').hidden = combined;
        pair.querySelector('[data-finish-pair-first]').classList.toggle('is-active', index === 3);
        pair.querySelector('[data-finish-pair-last]').classList.toggle('is-active', index === 4);
      }
      this.querySelector('[data-finish-count]').textContent = `${number} / ${this.totalLabel}`;
      this.querySelector('[data-finish-label]').textContent = this.steps[index].dataset.finishName;
      this.target.toggleAttribute('data-concealed', this.steps[index].dataset.concealed === 'true');
      const location = this.querySelector('[data-finish-location]');
      if (location) location.textContent = this.steps[index].dataset.focusLabel || '';
      if (announce && changed) this.querySelector('[data-finish-status]').textContent = `${this.steps[index].dataset.finishName}, ${index + 1} of ${this.steps.length}.`;
    }

    paint(point, markers = []) {
      if (!this.width || !this.height) return;
      this.currentPoint = point;
      this.hasPainted = true;
      this.classList.add('has-story-frame');
      const scale = this.reduced ? this.overviewPoint.scale : point.scale;
      const scaledWidth = this.imageWidth * scale;
      const scaledHeight = this.imageHeight * scale;
      const tx = scaledWidth <= this.width ? (this.width - scaledWidth) / 2 : clamp(this.width / 2 - scaledWidth * point.x, this.width - scaledWidth, 0);
      const ty = scaledHeight <= this.height ? (this.height - scaledHeight) / 2 : clamp(this.height / 2 - scaledHeight * point.y, this.height - scaledHeight, 0);
      this.camera.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
      const visibleMarkers = markers.filter(marker => marker.index >= 0);
      const primary = visibleMarkers.find(marker => marker.index === this.active) || visibleMarkers[0];
      const secondary = visibleMarkers.find(marker => marker !== primary);
      this.paintMarker(this.target, this.leader, primary, tx, ty, scaledWidth, scaledHeight);
      this.paintMarker(this.secondaryTarget, this.secondaryLeader, secondary, tx, ty, scaledWidth, scaledHeight);
      this.focusVisible = visibleMarkers.some(marker => marker.opacity > .01);
      this.classList.toggle('is-focused', this.focusVisible);
      const label = this.querySelector('.knox-finish-story__photo-label');
      if (label) label.style.opacity = this.active >= 0 ? String(primary?.opacity || 0) : '0';
    }

    paintMarker(target, leader, marker, tx, ty, scaledWidth, scaledHeight) {
      if (!target) return;
      if (!marker || marker.opacity <= .001) {
        target.style.opacity = '0';
        if (leader) leader.style.opacity = '0';
        return;
      }
      const focused = this.points[marker.index];
      const markerX = tx + scaledWidth * focused.x;
      const markerY = ty + scaledHeight * focused.y;
      const step = this.steps[marker.index];
      const offsetX = Number(step.dataset.markerOffsetX) || 0;
      const offsetY = Number(step.dataset.markerOffsetY) || 0;
      const offset = offsetX !== 0 || offsetY !== 0;
      const badgeX = offset ? clamp(markerX + offsetX, 30, this.width - 30) : markerX;
      const badgeY = offset ? clamp(markerY + offsetY, 30, this.height - 30) : markerY;
      const visible = markerX > 16 && markerX < this.width - 16 && markerY > 16 && markerY < this.height - 16;
      const opacity = visible ? marker.opacity : 0;
      const number = String(marker.index + 1).padStart(2, '0');
      const numberElement = target.querySelector('[data-finish-number]');
      if (numberElement) numberElement.textContent = number;
      else target.textContent = number;
      target.toggleAttribute('data-concealed', step.dataset.concealed === 'true');
      target.style.transform = `translate3d(${badgeX.toFixed(2)}px, ${badgeY.toFixed(2)}px, 0) translate(-50%, -50%)`;
      target.style.opacity = opacity.toFixed(3);
      if (leader) {
        const dx = badgeX - markerX;
        const dy = badgeY - markerY;
        const distance = Math.hypot(dx, dy);
        const gap = 8;
        const length = Math.max(0, distance - gap - 26);
        const leaderX = markerX + dx * gap / (distance || 1);
        const leaderY = markerY + dy * gap / (distance || 1);
        leader.style.width = `${length.toFixed(2)}px`;
        leader.style.transform = `translate3d(${leaderX.toFixed(2)}px, ${leaderY.toFixed(2)}px, 0) rotate(${Math.atan2(dy, dx)}rad)`;
        leader.style.opacity = offset && length > 0 ? opacity.toFixed(3) : '0';
      }
    }

    select(index) {
      index = Math.round(clamp(index, -1, this.lastIndex));
      if (!this.compact) {
        this.measure();
        const centre = index < 0 ? this.overviewCentre : this.centres[index];
        const top = scrollY + this.getBoundingClientRect().top + centre - this.anchor;
        window.scrollTo({ top: Math.max(0, top), behavior: this.reduced ? 'instant' : 'smooth' });
        return;
      }
      if (index === this.active && !this.motionFrame) return;
      const previous = this.currentPoint || this.overviewPoint;
      const previousIndex = this.active;
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      this.setActive(index, true);
      this.measure();
      const destination = index < 0 ? this.overviewPoint : this.points[index];
      if (this.reduced) {
        this.paint(destination, [{ index, opacity: index >= 0 ? 1 : 0 }]);
        return;
      }
      const started = performance.now();
      const tick = now => {
        if (!this.isConnected || !this.controller) return;
        const progress = clamp((now - started) / 760);
        const amount = ease(progress);
        this.paint(blend(previous, destination, amount), this.transitionMarkers(previousIndex, index, amount));
        if (progress < 1) {
          this.motionFrame = requestAnimationFrame(tick);
        } else {
          this.motionFrame = 0;
          this.schedule(false);
        }
      };
      this.motionFrame = requestAnimationFrame(tick);
    }
  }
  customElements.define('knox-finish-story', KnoxFinishStory);
})();


